import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiConfig } from "@/config/api.config";
import { ApiHookProps } from '@/@types/api.types';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';
import { SetTwoFactorEnabledRequestDto, SetTwoFactorEnabledResponseDto } from '@/features/account/types/account-dto';
import EToast from '@/components/everridge/EToast';
import { t } from 'i18next';

type Props = {
    send?: boolean;
} & ApiHookProps;

export const useTwoFactorAuthApi = ({ send }: Props = {
    send: false
}) => {
    const axiosInstance = useAxiosAuth();
    const queryClient = useQueryClient();

    const {
        data:twoFactorAuthCode,
        isLoading: isLoadingTwoFactorAuthCode,
        isError: isErrorTwoFactorAuthCode,
    } = useQuery({
        queryKey: [ApiConfig.queries.features.accounts.queryKeys.twoFactorAuth, send],
        queryFn: async (): Promise<SetTwoFactorEnabledResponseDto> => {
            const url = ApiConfig.queries.features.accounts.endpoints.getTwoFactorAuthCode;
            const response = await axiosInstance.get<SetTwoFactorEnabledResponseDto>(url);
            return response.data;
        },
        enabled: send,
    });

    const {
        mutateAsync: setTwoFactorAuth, 
        isPending: isSettingTwoFactorAuth, 
        isError: isErrorSettingTwoFactorAuth
    } = useMutation({
        mutationFn: async (dto: SetTwoFactorEnabledRequestDto) => {
            const response = await axiosInstance.post(
                ApiConfig.queries.features.accounts.endpoints.setTwoFactorAuth, dto
            );  
            return response.data;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ 
                queryKey: [ApiConfig.queries.features.accounts.queryKeys.myProfile] 
            });
            EToast({
                variant: 'success',
                message: t('Successfully change two-factor authentication settings.'), 
            });
        },
        onError: () => {
            EToast({
                variant: 'error',
                message: t('Error occurred during changing two-factor authentication settings. Try again later.'), 
            });
        }
    });

    return {
        twoFactorAuthCode,
        isLoadingTwoFactorAuthCode,
        isErrorTwoFactorAuthCode,
        setTwoFactorAuth, 
        isSettingTwoFactorAuth, 
        isErrorSettingTwoFactorAuth
    }
};